import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

const productsData = [
	{
		id: 1,
		name: "Shady Lane Curtain",
		subtitle: "Ag Curtain Systems & Components",
		url: "/products/shady-lane",
	},
	{
		id: 2,
		name: "Secco Chimneys",
		subtitle: "Barn Exhaust Ventilation Systems",
		url: "/products/secco-chimneys",
	},
	{
		id: 3,
		name: "Fans",
		subtitle: "Industrial Dairy Barn Ventilation",
		url: "/products/Fans",
	},
	{
		id: 4,
		name: "Freestalls",
		subtitle: "Livestock Housing & Comfort",
		url: "/products/Freestalls",
	},
	{
		id: 5,
		name: "EXL Lockups",
		subtitle: "Barn Lock & Control Hardware",
		url: "/products/EXL%20Lockups",
	},
	{
		id: 6,
		name: "Stainless Steel Tip Tanks",
		subtitle: "Sanitation Watering Systems",
		url: "/products/Stainless%20Steel%20Tip%20Tanks",
	},
	{
		id: 7,
		name: "Auto Vent Controllers",
		subtitle: "Automated Climate Control Hardware",
		url: "/products/Auto%20Vent%20Controllers",
	},
];

const navLinkClass = ({ isActive }) =>
	`flex items-end border-b-[3px] px-2.5 py-2.5 font-overpass text-[clamp(12px,1vw,14px)] ${
		isActive
			? "border-primary text-primary"
			: "border-transparent text-[#555] hover:border-primary hover:text-primary"
	}`;

function SearchResults({ items, activeIndex, onActiveIndexChange, onProductClick }) {
	return (
		<div className="absolute right-0 top-full z-[1000] mt-2 w-full overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-[0_16px_40px_rgba(15,23,42,0.12)] md:w-[400px]">
			<div className="flex items-center justify-between px-4 py-2.5">
				<p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">
					Matching Equipment ({items.length})
				</p>
				<p className="text-[11px] text-slate-400">ESC to clear</p>
			</div>
			<div className="max-h-[360px] overflow-y-auto border-t border-gray-100">
				{items.length > 0 ? (
					items.map((product, index) => {
						const isActive = index === activeIndex;

						return (
							<button
								key={product.id}
								type="button"
								onMouseDown={(event) => event.preventDefault()}
								onMouseEnter={() => onActiveIndexChange(index)}
								onClick={() => onProductClick(product.url)}
								className={`relative flex w-full items-center gap-3 border-b border-gray-100 px-4 py-3.5 text-left last:border-b-0 ${
									isActive ? "bg-[#F4F7FF]" : "bg-white"
								}`}
							>
								{isActive && (
									<span className="absolute inset-y-0 left-0 w-[3px] bg-primary" />
								)}
								<span className="min-w-0 flex-1">
									<span
										className={`block text-[15px] font-semibold ${
											isActive
												? "text-primary"
												: "text-slate-800"
										}`}
									>
										{product.name}
									</span>
									<span className="mt-0.5 block text-[13px] text-slate-400">
										{product.subtitle}
									</span>
								</span>
								<svg
									className={`h-4 w-4 shrink-0 ${
										isActive
											? "text-primary"
											: "text-slate-300"
									}`}
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2.25"
									strokeLinecap="round"
									strokeLinejoin="round"
									aria-hidden="true"
								>
									<path d="M9 6l6 6-6 6" />
								</svg>
							</button>
						);
					})
				) : (
					<p className="px-4 py-3.5 text-sm text-slate-400">
						No products found
					</p>
				)}
			</div>
		</div>
	);
}

function SearchBar({
	searchTerm,
	onSearch,
	onFocus,
	onBlur,
	onClear,
	filteredProducts,
	suggestedProducts,
	onProductClick,
	className = "",
}) {
	const [activeIndex, setActiveIndex] = useState(0);
	const items = searchTerm ? filteredProducts : suggestedProducts;
	const isOpen = Boolean(searchTerm) || suggestedProducts.length > 0;

	useEffect(() => {
		setActiveIndex(0);
	}, [searchTerm, items.length]);

	const handleKeyDown = (event) => {
		if (event.key === "Escape") {
			event.preventDefault();
			onClear();
			return;
		}

		if (!isOpen || items.length === 0) {
			return;
		}

		if (event.key === "ArrowDown") {
			event.preventDefault();
			setActiveIndex((index) => (index + 1) % items.length);
		} else if (event.key === "ArrowUp") {
			event.preventDefault();
			setActiveIndex((index) => (index - 1 + items.length) % items.length);
		} else if (event.key === "Enter") {
			event.preventDefault();
			const product = items[activeIndex];
			if (product) {
				onProductClick(product.url);
			}
		}
	};

	return (
		<div className={`relative ${className}`}>
			<form
				className={`flex h-10 w-full items-center gap-2 rounded-full border-2 bg-white pl-3 pr-3 md:w-[228px] ${
					isOpen ? "border-primary" : "border-gray-300"
				}`}
				onSubmit={(event) => event.preventDefault()}
			>
				<svg
					className={`h-4 w-4 shrink-0 ${
						isOpen ? "text-primary" : "text-gray-500"
					}`}
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
					aria-hidden="true"
				>
					<circle cx="11" cy="11" r="7" />
					<path d="M21 21l-4.3-4.3" />
				</svg>
				<input
					type="search"
					placeholder="Search equipment..."
					aria-label="Search equipment"
					value={searchTerm}
					onChange={onSearch}
					onFocus={onFocus}
					onBlur={onBlur}
					onKeyDown={handleKeyDown}
					className="w-full border-none bg-transparent font-sans text-sm text-slate-700 placeholder:text-slate-400 outline-none"
				/>
			</form>
			{isOpen && (
				<SearchResults
					items={items}
					activeIndex={activeIndex}
					onActiveIndexChange={setActiveIndex}
					onProductClick={onProductClick}
				/>
			)}
		</div>
	);
}

export default function Header() {
	const navigate = useNavigate();
	const [expanded, setExpanded] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [filteredProducts, setFilteredProducts] = useState([]);
	const [suggestedProducts, setSuggestedProducts] = useState([]);

	const handleClose = () => setExpanded(false);

	const handleClearSearch = () => {
		setSearchTerm("");
		setFilteredProducts([]);
		setSuggestedProducts([]);
	};

	const handleProductClick = (productUrl) => {
		navigate(productUrl);
		handleClose();
		handleClearSearch();
	};

	const handleSearch = (event) => {
		const term = event.target.value;
		setSearchTerm(term);
		if (term) {
			const filtered = productsData.filter((product) =>
				product.name.toLowerCase().includes(term.toLowerCase())
			);
			setFilteredProducts(filtered);
		} else {
			setFilteredProducts([]);
		}
	};

	const handleFocus = () => {
		if (!searchTerm) {
			setSuggestedProducts(productsData);
		}
	};

	const handleBlur = () => {
		setTimeout(() => {
			setSearchTerm("");
			setFilteredProducts([]);
			setSuggestedProducts([]);
		}, 300);
	};

	useEffect(() => {
		document.body.style.overflow = expanded ? "hidden" : "";
		return () => {
			document.body.style.overflow = "";
		};
	}, [expanded]);

	const links = [
		{ to: "/", label: "HOME" },
		{ to: "/products", label: "PRODUCTS" },
		{ to: "/gallery", label: "GALLERY" },
	];

	return (
		<header className="relative bg-white">
			<nav className="relative mx-auto flex max-w-7xl items-center justify-between px-6 py-3 sm:px-8 lg:px-12">
				<NavLink to="/" className="relative z-10 shrink-0">
					<img
						src={process.env.PUBLIC_URL + "/images/logo.png"}
						alt="Mid-State Ag Supply"
						className="h-[50px] w-auto"
					/>
				</NavLink>

				<div className="absolute left-1/2 hidden -translate-x-1/2 items-center md:flex">
					{links.map((link) => (
						<NavLink
							key={link.to}
							to={link.to}
							className={navLinkClass}
						>
							{link.label}
						</NavLink>
					))}
				</div>

				<SearchBar
					className="hidden md:block"
					searchTerm={searchTerm}
					onSearch={handleSearch}
					onFocus={handleFocus}
					onBlur={handleBlur}
					onClear={handleClearSearch}
					filteredProducts={filteredProducts}
					suggestedProducts={suggestedProducts}
					onProductClick={handleProductClick}
				/>

				<button
					type="button"
					className="p-2 text-xl text-[#555] md:hidden"
					aria-label="Toggle menu"
					aria-expanded={expanded}
					onClick={() => setExpanded((open) => !open)}
				>
					<i className="fa-solid fa-bars"></i>
				</button>
			</nav>

			<div
				className={`fixed inset-0 z-[1050] bg-black/40 transition-opacity md:hidden ${
					expanded
						? "opacity-100"
						: "pointer-events-none opacity-0"
				}`}
				onClick={handleClose}
			/>
			<aside
				className={`fixed right-0 top-0 z-[1051] flex h-full w-80 max-w-full flex-col bg-white p-4 shadow-xl transition-transform duration-300 md:hidden ${
					expanded ? "translate-x-0" : "translate-x-full"
				}`}
			>
				<div className="mb-4 flex items-center justify-between">
					<h2 className="text-lg font-semibold">Menu</h2>
					<button
						type="button"
						className="p-2 text-xl text-[#555]"
						aria-label="Close menu"
						onClick={handleClose}
					>
						<i className="fa-solid fa-xmark"></i>
					</button>
				</div>
				<SearchBar
					className="mb-4 w-full"
					searchTerm={searchTerm}
					onSearch={handleSearch}
					onFocus={handleFocus}
					onBlur={handleBlur}
					onClear={handleClearSearch}
					filteredProducts={filteredProducts}
					suggestedProducts={suggestedProducts}
					onProductClick={handleProductClick}
				/>
				<nav className="flex flex-col items-end">
					{links.map((link) => (
						<NavLink
							key={link.to}
							to={link.to}
							className={navLinkClass}
							onClick={handleClose}
						>
							{link.label}
						</NavLink>
					))}
				</nav>
			</aside>
		</header>
	);
}
